import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Search, Trash2, Pencil, MapPin, Building2, DoorOpen } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export default function Properties() {
  const { t } = useLang();
  const { user, can } = useAuth();
  // Permission-aware helpers — matrix decides, client role always retains its
  // own-property CRUD legacy privilege regardless of the matrix state.
  const canCreate = user?.role === "client" || can("properties", "create");
  const canEdit   = user?.role === "client" || can("properties", "edit");
  const canDelete = user?.role === "client" || can("properties", "delete");
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [clients, setClients] = useState([]);
  const [unitModal, setUnitModal] = useState(null); // { mode: 'new'|'edit', property, unit?, label, notes }
  const [savingUnit, setSavingUnit] = useState(false);

  const load = () => api.get("/properties").then(setProperties);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    load();
    if (["admin", "manager"].includes(user.role)) api.get("/clients").then(setClients).catch(() => {});
  }, []);
  // Deep-link support: /properties?new=1 opens the "new property" modal
  // (used by the client home's "Add property" quick button).
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openNew = () => { setEditing(null); setForm({ name: "", address: "", city: "", client_name: "", client_id: "", image_url: "", notes: "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const saveProperty = async () => {
    if (!form.name?.trim() || !form.address?.trim()) return toast.error(t("propertyName"));
    try {
      const payload = { ...form, client_id: form.client_id || (form.client_name ? form.client_name.toLowerCase().replace(/\s+/g, "-") : null) };
      delete payload.units; delete payload.units_count;
      if (editing) await api.put(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch { toast.error(t("error")); }
  };
  const removeProperty = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.del(`/properties/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const openNewUnit = (p) => setUnitModal({ mode: "new", property: p, label: `Unit ${(p.units_count || 0) + 1}`, notes: "", tenant_name: "", tenant_phone: "" });
  const openEditUnit = (p, u) => setUnitModal({ mode: "edit", property: p, unit: u, label: u.label || "", notes: u.notes || "", tenant_name: u.tenant_name || "", tenant_phone: u.tenant_phone || "" });

  const saveUnit = async () => {
    if (!unitModal) return;
    const label = (unitModal.label || "").trim();
    if (!label) { toast.error(t("unitLabel")); return; }
    setSavingUnit(true);
    try {
      const payload = {
        label,
        notes: unitModal.notes || "",
        tenant_name: unitModal.tenant_name || "",
        tenant_phone: unitModal.tenant_phone || "",
      };
      if (unitModal.mode === "new") {
        await api.post("/units", { property_id: unitModal.property.id, ...payload });
        toast.success(t("saved"));
      } else {
        await api.put(`/units/${unitModal.unit.id}`, payload);
        toast.success(t("saved"));
      }
      setUnitModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally {
      setSavingUnit(false);
    }
  };

  const removeUnit = async (u) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.del(`/units/${u.id}`);
      toast.success(t("deleted"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
  };

  const filtered = properties.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.address?.toLowerCase().includes(search.toLowerCase()) || p.client_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("properties")}</h1>
        {canCreate && (
          <button data-testid="new-property-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
            <Plus className="w-4 h-4" /> {t("newProperty")}
          </button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="property-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      <div className="space-y-4">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`property-card-${p.id}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-48 h-32 sm:h-auto bg-slate-100 shrink-0 overflow-hidden">
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-8 h-8" /></div>}
              </div>
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">{p.name}</h3>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.address}{p.city ? `, ${p.city}` : ""}</div>
                    <div className="text-xs text-slate-400 mt-1">{p.client_name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">{p.units_count} {t("unitsCount")}</span>
                    {(canEdit || canDelete) && (
                      <>
                        {canEdit && (
                          <button data-testid={`edit-property-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                        )}
                        {canDelete && (
                          <button data-testid={`delete-property-${p.id}`} onClick={() => removeProperty(p.id)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Units within this address */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex flex-wrap gap-2">
                    {(p.units || []).map((u) => (
                      <span key={u.id} data-testid={`unit-chip-${u.id}`} className="group inline-flex items-center gap-1.5 text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg pl-2.5 pr-1.5 py-1.5 text-slate-700">
                        <DoorOpen className="w-3.5 h-3.5 text-slate-400" />{u.label}
                        {(canEdit || canDelete) && (
                          <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            {canEdit && (
                              <button onClick={() => openEditUnit(p, u)} className="p-0.5 hover:text-slate-900"><Pencil className="w-3 h-3" /></button>
                            )}
                            {canDelete && (
                              <button onClick={() => removeUnit(u)} className="p-0.5 hover:text-red-600"><X className="w-3 h-3" /></button>
                            )}
                          </span>
                        )}
                      </span>
                    ))}
                    {(p.units || []).length === 0 && <span className="text-xs text-slate-400">{t("noUnits")}</span>}
                    {canCreate && (
                      <button data-testid={`add-unit-${p.id}`} onClick={() => openNewUnit(p)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 border border-dashed border-blue-300 rounded-lg px-2.5 py-1.5 hover:bg-blue-50">
                        <Plus className="w-3.5 h-3.5" /> {t("addUnit")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-400 py-12 text-center">{t("noData")}</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("edit") : t("newProperty")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("propertyName")}</label>
                <input data-testid="property-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("address")}</label>
                <AddressAutocomplete
                  key={editing?.id || "new"}
                  testId="property-address"
                  value={form.address || ""}
                  placeholder={t("address")}
                  onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                  onSelect={(p) => setForm((f) => ({
                    ...f,
                    address: p.address,
                    city: [p.city, p.state, p.postcode].filter(Boolean).join(", "),
                    lat: p.lat,
                    lng: p.lng,
                    name: f.name || p.address,
                  }))}
                />
                {(form.lat && form.lng) ? <div className="text-[11px] text-slate-400 mt-1">{Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}</div> : null}
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("city")}</label>
                <input data-testid="property-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Image URL</label>
                <input data-testid="property-image_url" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tenantName") || "Tenant Name"}</label>
                  <input data-testid="property-tenant_name" value={form.tenant_name || ""} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} className={inputCls} placeholder="Optional" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tenantPhone") || "Tenant Phone"}</label>
                  <input data-testid="property-tenant_phone" value={form.tenant_phone || ""} onChange={(e) => setForm({ ...form, tenant_phone: e.target.value })} className={inputCls} placeholder="Optional" />
                </div>
                <div className="sm:col-span-2 -mt-2 text-[11px] text-slate-400">{t("tenantAutoFillHint") || "If left blank now, this will be filled automatically when a Service Call is created with the tenant's info."}</div>
              </div>
              {user.role !== "client" && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("client")}</label>
                  <select data-testid="property-client" value={form.client_id || ""} className={inputCls}
                    onChange={(e) => {
                      const c = clients.find((x) => x.id === e.target.value);
                      setForm({ ...form, client_id: e.target.value || null, client_name: c ? c.name : "" });
                    }}>
                    <option value="">{t("noClientOpt")}</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    {form.client_id && !clients.some((c) => c.id === form.client_id) && (
                      <option value={form.client_id}>{form.client_name || form.client_id}</option>
                    )}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="property-save" onClick={saveProperty} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {unitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => !savingUnit && setUnitModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{unitModal.mode === "new" ? t("addUnit") : t("edit")}</h2>
              <button data-testid="unit-modal-close" onClick={() => setUnitModal(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-xs text-slate-500">{unitModal.property?.name}{unitModal.property?.address ? ` • ${unitModal.property.address}` : ""}</div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("unitLabel")}</label>
                <input
                  autoFocus
                  data-testid="unit-label-input"
                  value={unitModal.label}
                  onChange={(e) => setUnitModal({ ...unitModal, label: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter" && !savingUnit) saveUnit(); }}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("notes") || "Notes"}</label>
                <textarea
                  data-testid="unit-notes-input"
                  value={unitModal.notes}
                  onChange={(e) => setUnitModal({ ...unitModal, notes: e.target.value })}
                  rows={2}
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tenantName") || "Tenant Name"}</label>
                  <input
                    data-testid="unit-tenant-name"
                    value={unitModal.tenant_name || ""}
                    onChange={(e) => setUnitModal({ ...unitModal, tenant_name: e.target.value })}
                    className={inputCls}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("tenantPhone") || "Tenant Phone"}</label>
                  <input
                    data-testid="unit-tenant-phone"
                    value={unitModal.tenant_phone || ""}
                    onChange={(e) => setUnitModal({ ...unitModal, tenant_phone: e.target.value })}
                    className={inputCls}
                    placeholder="Optional"
                  />
                </div>
                <div className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">{t("tenantAutoFillHint") || "If left blank now, this will be filled automatically when a Service Call is created with the tenant's info."}</div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button disabled={savingUnit} onClick={() => setUnitModal(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50 disabled:opacity-50">{t("cancel")}</button>
              <button data-testid="unit-save" disabled={savingUnit} onClick={saveUnit} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">{savingUnit ? "..." : t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
